import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Card, Alert, Collapse } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { createAttractionActivity, toggleOption, isOptionSelected as checkOptionSelected } from '../../shared/activityUtils';
import { DEFAULT_CURRENCY } from '../../shared/constants';
import { getSupportedCurrenciesSync } from '../../shared/currencyHelpers';
import { useModalBackHandler } from '../../shared/modalBackHandler';
import { formatForDateTimeLocal } from '../../shared/utils';
import { isRTL } from '../../i18n/config';
import { getCachedAccountData } from '../../shared/accountCache';
import LinkIconDropdown from '../LinkIconDropdown';

export default function AttractionModal({
  show,
  onHide,
  activity,
  onSave,
  dayId,
  isNew = false,
  parsedData,
  planLanguage = 'en',
  onSwitchToAI,
  accountSettings
}) {
  const { t, i18n } = useTranslation();
  const [currencies, setCurrencies] = useState(getSupportedCurrenciesSync());
  const [showGenerateActivityTip, setShowGenerateActivityTip] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [accountImages, setAccountImages] = useState([]);
  
  // Check if plan was created in non-English language
  const isNonEnglishPlan = planLanguage && planLanguage !== 'en';
  
  // Initialize with attraction-specific fields
  const [formData, setFormData] = useState({
    id: '',
    type: 'attraction',
    name: '',
    nameInEnglish: '',
    description: '',
    notes: '',
    startDateTime: '',
    endDateTime: '',
    cost: { value: '', currency: DEFAULT_CURRENCY },
    links: [],
    images: [],
    location: { address: '', addressInEnglish: '', latitude: '', longitude: '' },
    completed: false,
    option: [] // Can be array [1,2,3], "all", or [] (empty array means all options)
  });
  
  // Load currencies on mount
  useEffect(() => {
    const loadCurrencies = async () => {
      const { getAllCurrencies } = await import('../../shared/currencyService');
      const loadedCurrencies = await getAllCurrencies();
      if (loadedCurrencies && loadedCurrencies.length > 0) {
        setCurrencies(loadedCurrencies.map(c => ({
          code: c.code,
          name: c.name || c.code
        })));
      }
    };
    loadCurrencies();
  }, []);

  // Check if we should show the generate activity tip (only once, only for new activities)
  useEffect(() => {
    if (!show || !isNew || activity) return; // Only show for new activities, not when editing
    
    const hasSeenTip = localStorage.getItem('tripsy_has_seen_generate_activity_tip');
    if (!hasSeenTip) {
      setShowGenerateActivityTip(true);
    }
  }, [show, isNew, activity]);

  // Load activity data when modal is opened
  useEffect(() => {
    // Only reset when modal is shown
    if (!show) return;
    
    if (activity) {
      // Edit existing activity
      setFormData({
        id: activity.id || '',
        type: 'attraction',
        name: activity.name || '',
        nameInEnglish: activity.nameInEnglish || '',
        description: activity.description || '',
        notes: activity.notes || '',
        startDateTime: activity.startDateTime || '',
        endDateTime: activity.endDateTime || '',
        cost: activity.cost || { value: '', currency: parsedData?.currency || DEFAULT_CURRENCY },
        links: activity.links || [],
        images: activity.images || [],
        location: {
          address: activity.location?.address || '',
          addressInEnglish: activity.location?.addressInEnglish || '',
          latitude: activity.location?.latitude || '',
          longitude: activity.location?.longitude || ''
        },
        completed: activity.completed || false,
        option: activity.option !== undefined ? activity.option : [] // Can be array [1,2,3], "all", or [] (empty array defaults to option 1)
      });
    } else if (isNew) {
      // Create new attraction with default values
      
      // Set default date based on selected day
      let defaultStartDate = '';
      
      // Find the day data to get its date
      const dayData = parsedData?.days?.find(day => day.id === dayId);
      if (dayData && dayData.date) {
        // Set default date to the selected day
        defaultStartDate = dayData.date;
      }
      
      // Reset form with defaults
      setFormData({
        id: '',
        type: 'attraction',
        name: '',
        nameInEnglish: '',
        description: '',
        notes: '',
        startDateTime: defaultStartDate ? `${defaultStartDate}T09:00:00.000Z` : '',
        endDateTime: defaultStartDate ? `${defaultStartDate}T11:00:00.000Z` : '',
        cost: { value: '', currency: parsedData?.currency || DEFAULT_CURRENCY },
        links: [],
        images: [],
        location: { address: '', addressInEnglish: '', latitude: '', longitude: '' },
        option: [] // Default to option 1 (empty array means option 1)
      });
    }
  }, [show, activity, dayId, isNew, parsedData]);
  
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'cost.value') {
      setFormData(prev => ({
        ...prev,
        cost: {
          ...prev.cost,
          value: value
        }
      }));
    } else if (name === 'cost.currency') {
      setFormData(prev => ({
        ...prev,
        cost: {
          ...prev.cost,
          currency: value
        }
      }));
    } else if (name === 'location.address' || name === 'location.addressInEnglish' || name === 'location.latitude' || name === 'location.longitude') {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  // Add a new link field
  const addLinkField = () => {
    setFormData(prev => ({
      ...prev,
      links: [...prev.links, { name: '', url: '', isPrivate: true, icon: '' }]
    }));
  };
  
  // Remove a link field
  const removeLinkField = (index) => {
    setFormData(prev => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index)
    }));
  };
  
  // Handle link input changes
  const handleLinkChange = (index, field, value) => {
    setFormData(prev => {
      const updatedLinks = [...prev.links];
      updatedLinks[index] = {
        ...updatedLinks[index],
        [field]: value
      };
      return {
        ...prev,
        links: updatedLinks
      };
    });
  };
  
  // Handle adding a new image
  const handleAddImage = () => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, { type: '', url: '' }]
    }));
  };
  
  // Handle removing an image
  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };
  
  // Handle image field changes
  const handleImageChange = (index, field, value) => {
    setFormData(prev => {
      const updatedImages = [...prev.images];
      updatedImages[index] = {
        ...updatedImages[index],
        [field]: value
      };
      return {
        ...prev,
        images: updatedImages
      };
    });
  };

  // Handle option changes (multi-select)
  const handleOptionChange = (optionValue) => {
    setFormData(prev => ({
      ...prev,
      option: toggleOption(prev.option, optionValue)
    }));
  };

  // Check if an option is selected
  const isOptionSelected = (optionValue) => {
    return checkOptionSelected(formData.option, optionValue);
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Create new attraction activity
    const updatedActivity = createAttractionActivity({
      ...formData,
      id: formData.id || undefined
    });
    
    // Save the activity and close the modal
    onSave(updatedActivity, dayId);
    onHide();
  };
  
  // Use our custom hook to handle browser back button
  useModalBackHandler(show, onHide, true, "Changes you made may not be saved. Are you sure you want to leave?");
  
  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isNew ? t('planDetail.activityModals.addAttraction') : t('planDetail.activityModals.editAttraction')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 py-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {showGenerateActivityTip && (
          <Alert variant="info" dismissible onClose={() => {
            setShowGenerateActivityTip(false);
            localStorage.setItem('tripsy_has_seen_generate_activity_tip', 'true');
          }} className="mb-3">
            <Alert.Heading>
              <i className="bi bi-lightbulb me-2"></i>
              {t('planDetail.activityModals.tipTitle')}
            </Alert.Heading>
            <p className="mb-0">
              {t('planDetail.activityModals.tipDescription')}
            </p>
            {onSwitchToAI && (
              <div className="mt-2">
                <Button variant="primary" size="sm" onClick={() => {
                  setShowGenerateActivityTip(false);
                  localStorage.setItem('tripsy_has_seen_generate_activity_tip', 'true');
                  onSwitchToAI();
                }}>
                  <i className="bi bi-magic me-1"></i>
                  {t('planDetail.activityModals.tryAddActivityWithAI')}
                </Button>
              </div>
            )}
          </Alert>
        )}
        <Form id="attraction-form" onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">
              {t('planDetail.activityModals.name')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control 
              type="text" 
              name="name" 
              value={formData.name} 
              onChange={handleInputChange}
              placeholder={t('planDetail.activityModals.placeholder.attractionName')}
              required
            />
          </Form.Group>
          
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t('planDetail.activityModals.startDateTime')}</Form.Label>
                <Form.Control 
                  type="datetime-local" 
                  name="startDateTime" 
                  value={formatForDateTimeLocal(formData.startDateTime)}
                  onChange={(e) => {
                    const value = e.target.value ? `${e.target.value}:00.000Z` : '';
                    handleInputChange({
                      target: {
                        name: 'startDateTime',
                        value
                      }
                    });
                  }}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold">{t('planDetail.activityModals.endDateTime')}</Form.Label>
                <Form.Control 
                  type="datetime-local" 
                  name="endDateTime" 
                  value={formatForDateTimeLocal(formData.endDateTime)}
                  onChange={(e) => {
                    const value = e.target.value ? `${e.target.value}:00.000Z` : '';
                    handleInputChange({
                      target: {
                        name: 'endDateTime',
                        value
                      }
                    });
                  }}
                />
              </Form.Group>
            </Col>
          </Row>
          
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">{t('planDetail.activityModals.description')}</Form.Label>
            <Form.Control 
              as="textarea" 
              rows={3} 
              name="description" 
              value={formData.description} 
              onChange={handleInputChange}
              placeholder={t('planDetail.activityModals.placeholder.attractionDescription')}
            />
          </Form.Group>
          
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">{t('planDetail.activityModals.cost')}</Form.Label>
                <Form.Control 
                  type="number" 
                  name="cost.value" 
                  value={formData.cost.value} 
                  onChange={handleInputChange}
                  placeholder={t('planDetail.activityModals.placeholder.cost')}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-bold">{t('planDetail.activityModals.currency')}</Form.Label>
                <Form.Select 
                  name="cost.currency" 
                  value={formData.cost.currency} 
                  onChange={handleInputChange}
                >
                  {currencies.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.code} - {currency.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          <Card className="mb-3">
            <Card.Header>
              <div className="d-flex align-items-center">
                <strong className="me-2">{t('planDetail.activityModals.location')}</strong>
                <i 
                  className="bi bi-question-circle-fill text-primary" 
                  style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                  data-tooltip-id="attraction-location-tooltip"
                  data-tooltip-html={`<div><strong>${t('planDetail.activityModals.locationTooltip.title')}</strong><br/><br/>${t('planDetail.activityModals.locationTooltip.description')}</div>`}
                ></i>
                <Tooltip id="attraction-location-tooltip" place="right" className="activity-modal-tooltip" />
              </div>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>{t('planDetail.activityModals.address')}</Form.Label>
                <Form.Control 
                  type="text" 
                  name="location.address" 
                  value={formData.location.address} 
                  onChange={handleInputChange}
                  placeholder={t('planDetail.activityModals.placeholder.address')}
                />
              </Form.Group>
              
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('planDetail.activityModals.latitude')}</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="location.latitude" 
                      value={formData.location.latitude} 
                      onChange={handleInputChange}
                      placeholder={t('planDetail.activityModals.placeholder.latitudeExample')}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('planDetail.activityModals.longitude')}</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="location.longitude" 
                      value={formData.location.longitude} 
                      onChange={handleInputChange}
                      placeholder={t('planDetail.activityModals.placeholder.longitudeExample')}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          
          {/* Advanced Section */}
          <Card className="mb-3">
            <Card.Header 
              onClick={() => setShowAdvanced(!showAdvanced)}
              style={{ cursor: 'pointer' }}
              className="d-flex justify-content-between align-items-center"
            >
              <strong>{t('planDetail.activityModals.advanced')}</strong>
              <i className={`bi bi-chevron-${showAdvanced ? 'up' : 'down'}`}></i>
            </Card.Header>
            <Collapse in={showAdvanced}>
              <Card.Body>
                {/* Name in English - only if plan is not English */}
                {isNonEnglishPlan && (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold d-flex align-items-center">
                      <span className="me-2">{t('planDetail.activityModals.nameInEnglish')}</span>
                      <i 
                        className="bi bi-question-circle-fill text-primary" 
                        style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                        data-tooltip-id="attraction-name-english-tooltip"
                        data-tooltip-html={t('planDetail.activityModals.nameInEnglishTooltip')}
                      ></i>
                      <Tooltip id="attraction-name-english-tooltip" place="right" className="activity-modal-tooltip" />
                    </Form.Label>
                    <Form.Control 
                      type="text" 
                      name="nameInEnglish" 
                      value={formData.nameInEnglish} 
                      onChange={handleInputChange}
                      placeholder={t('planDetail.activityModals.placeholder.nameInEnglish')}
                      dir="ltr"
                    />
                  </Form.Group>
                )}
                
                {/* Address in English - only if plan is not English */}
                {isNonEnglishPlan && (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold d-flex align-items-center">
                      <span className="me-2">{t('planDetail.activityModals.addressInEnglish')}</span>
                      <i 
                        className="bi bi-question-circle-fill text-primary" 
                        style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                        data-tooltip-id="attraction-address-english-tooltip"
                        data-tooltip-html={t('planDetail.activityModals.addressInEnglishTooltip')}
                      ></i>
                      <Tooltip id="attraction-address-english-tooltip" place="right" className="activity-modal-tooltip" />
                    </Form.Label>
                    <Form.Control 
                      type="text" 
                      name="location.addressInEnglish" 
                      value={formData.location.addressInEnglish} 
                      onChange={handleInputChange}
                      placeholder={t('planDetail.activityModals.placeholder.addressInEnglish')}
                      dir="ltr"
                    />
                  </Form.Group>
                )}
                
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">{t('planDetail.activityModals.notes')}</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={2} 
                    name="notes" 
                    value={formData.notes} 
                    onChange={handleInputChange}
                    placeholder={t('planDetail.activityModals.placeholder.attractionNotes')}
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold d-flex align-items-center">
                    <span className="me-2">{t('planDetail.activityModals.itineraryOptions')}</span>
                    <i 
                      className="bi bi-question-circle-fill text-primary" 
                      style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                      data-tooltip-id="attraction-options-tooltip"
                      data-tooltip-html={t('planDetail.activityModals.optionTooltip')}
                    ></i>
                    <Tooltip id="attraction-options-tooltip" place="right" className="activity-modal-tooltip" />
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-3">
                    <Form.Check
                      type="checkbox"
                      id="option-1"
                      label={`${t('planDetail.days.option')} 1`}
                      checked={isOptionSelected('1')}
                      onChange={() => handleOptionChange('1')}
                    />
                    <Form.Check
                      type="checkbox"
                      id="option-2"
                      label={`${t('planDetail.days.option')} 2`}
                      checked={isOptionSelected('2')}
                      onChange={() => handleOptionChange('2')}
                    />
                    <Form.Check
                      type="checkbox"
                      id="option-3"
                      label={`${t('planDetail.days.option')} 3`}
                      checked={isOptionSelected('3')}
                      onChange={() => handleOptionChange('3')}
                    />
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold d-flex align-items-center">
                    <span className="me-2">{t('planDetail.activityModals.links')}</span>
                    <i 
                      className="bi bi-question-circle-fill text-primary" 
                      style={{ cursor: 'help', fontSize: '1.2rem', opacity: 1, color: '#0d6efd' }}
                      data-tooltip-id="attraction-links-tooltip"
                      data-tooltip-html={`<div style="font-weight: normal;"><strong>${t('planDetail.activityModals.linksTooltip.title')}</strong><br/><br/>${t('planDetail.activityModals.linksTooltip.description')}</div>`}
                    ></i>
                    <Tooltip id="attraction-links-tooltip" place="right" className="activity-modal-tooltip" />
                  </Form.Label>
                  {formData.links.map((link, index) => (
                    <Row key={index} className={`mb-3 align-items-center gap-2 ${index % 2 === 0 ? 'bg-light' : ''}`} style={{ padding: '0.5rem', borderRadius: '0.25rem' }}>
                      <Col xs={12} md={3}>
                        <Form.Control 
                          type="text" 
                          placeholder={t('planDetail.activityModals.linkName')} 
                          value={link.name} 
                          onChange={(e) => handleLinkChange(index, 'name', e.target.value)}
                        />
                      </Col>
                      <Col xs={12} md={4}>
                        <Form.Control 
                          type="url" 
                          placeholder={t('planDetail.activityModals.url')} 
                          value={link.url} 
                          onChange={(e) => handleLinkChange(index, 'url', e.target.value)}
                          dir="ltr"
                        />
                      </Col>
                      <Col xs={12} md={2}>
                        <LinkIconDropdown
                          value={link.icon ?? ''}
                          onChange={(val) => handleLinkChange(index, 'icon', val)}
                          accountImages={[...(accountSettings?.images || []), ...(accountImages || [])].filter((img, i, arr) => img?.type === 'icon' && img?.url && arr.findIndex((x) => x?.url === img?.url) === i)}
                          t={t}
                          size="sm"
                          ariaLabel={t('planDetail.activityModals.linkIcon', { defaultValue: 'Icon' })}
                        />
                      </Col>
                      <Col xs={12} md={3} className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="checkbox"
                          label={t('planDetail.activityModals.private')}
                          checked={link.isPrivate !== false}
                          onChange={(e) => handleLinkChange(index, 'isPrivate', e.target.checked)}
                          className="small mb-0 private-link-checkbox"
                        />
                        <Button 
                          variant="outline-danger" 
                          size="sm" 
                          onClick={() => removeLinkField(index)}
                          className={isRTL(i18n.language) ? 'me-auto' : 'ms-auto'}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={addLinkField}
                    className="mt-2"
                  >
                    <i className="bi bi-plus-circle me-1"></i> {t('planDetail.activityModals.addLink')}
                  </Button>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold">{t('planDetail.activityModals.images')}</Form.Label>
                  {formData.images.map((image, index) => (
                    <div key={index} className={`mb-3 ${index % 2 === 0 ? 'bg-light' : ''}`} style={{ padding: '0.5rem', borderRadius: '0.25rem' }}>
                      <Row className="mb-2 gap-2 align-items-center">
                        <Col xs={12} md={5}>
                          <Form.Control 
                            type="text" 
                            placeholder={t('planDetail.activityModals.title')} 
                            value={image.type} 
                            onChange={(e) => handleImageChange(index, 'type', e.target.value)}
                          />
                        </Col>
                        <Col xs={12} md={6}>
                          <Form.Control 
                            type="url" 
                            placeholder={t('planDetail.activityModals.imageUrl')} 
                            value={image.url} 
                            onChange={(e) => handleImageChange(index, 'url', e.target.value)}
                            dir="ltr"
                          />
                        </Col>
                        <Col xs={12} md={1} className="d-flex align-items-center">
                          <Button 
                            variant="outline-danger" 
                            size="sm" 
                            onClick={() => handleRemoveImage(index)}
                            className={isRTL(i18n.language) ? 'me-auto' : 'ms-auto'}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </Col>
                      </Row>
                      {image.url && (
                        <div className="mt-2">
                          <img 
                            src={image.url} 
                            alt={image.type || 'Preview'} 
                            className="img-thumbnail"
                            style={{ 
                              maxWidth: '150px', 
                              maxHeight: '100px',
                              objectFit: 'contain',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    onClick={handleAddImage}
                    className="mt-2"
                  >
                    <i className="bi bi-plus-circle me-1"></i> {t('planDetail.activityModals.addImage')}
                  </Button>
                </Form.Group>
              </Card.Body>
            </Collapse>
          </Card>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" type="submit" form="attraction-form">
          {isNew ? t('planDetail.activityModals.addAttraction') : t('planDetail.activityModals.saveChanges')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
} 